import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useActiveWorkerTask } from "@/hooks/useActiveWorkerTask";

/**
 * El candado de tarea en marcha, para el portal del TRABAJADOR.
 *
 * Regla: una tarea a la vez. En cuanto hay una en marcha, el trabajador se queda
 * en su ficha y no puede ir a ninguna otra pantalla hasta completarla o
 * detenerla. El motivo no es de diseño, es de datos: las tareas empezadas y
 * nunca cerradas dejan el parte a medias y no hay quien las recupere después.
 *
 * Va aquí, envolviendo a las rutas del trabajador, y no en cada pantalla: por
 * este punto pasan TODAS —la barra de abajo, un enlace de una notificación, el
 * botón de atrás del teléfono y el arranque en frío—, así que no queda ningún
 * camino por el que colarse. Y va dentro de la rama `role === "worker"`, así que
 * ningún otro rol llega siquiera a consultarlo.
 *
 * Dos decisiones que parecen detalles y no lo son:
 *
 * 1. Mientras la consulta no ha vuelto NO se encierra a nadie. Encerrar por una
 *    respuesta que todavía no está sería un fallo peor que el que esto arregla
 *    —basta un segundo de red mala para dejar a alguien mirando una ficha que no
 *    era—, y en cuanto vuelve, el desvío ocurre igual.
 * 2. `replace`: el desvío no apila entradas de historial. Si apilara, cada toque
 *    del botón de atrás dejaría una entrada nueva y el historial crecería sin
 *    parar mientras el trabajador insiste.
 */
export function WorkerTaskLock({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { activeTask, resolved } = useActiveWorkerTask(true);

  if (!resolved || !activeTask) return <>{children}</>;

  const lockedPath = `/worker/tasks/${activeTask.id}`;
  if (location.pathname === lockedPath) return <>{children}</>;

  return <Navigate to={lockedPath} replace />;
}
